import { useEffect, useState } from "react";
import { message, ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import "dayjs/locale/es";

import styles from "../ingresosPOStyles.module.css";
import OrdenesServicioPOHeader from "./OrdenesServicioPOHeader";
import OrdenesServicioPOCards from "./OrdenesServicioPOCards";
import OrdenesServicioPOFilters from "./components/OrdenesServicioPOFilters";
import OrdenesServicioPOTable from "./components/OrdenesServicioPOTable";
import RegistroProductosPOForm from "./components/RegistroProductosPOForm";
import LeerPoPdfModal from "./components/LeerPoPdfModal";
import dayjs from "dayjs";
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../../store/auth/selectors';
import { getOrdenesServicioPO, deleteOrdenServicioPO } from "../../../services/financiero/ingresosPO/ordenesServicioPOService";

const contiene = (texto, busqueda) => (texto || "").toString().toLowerCase().includes(busqueda.toLowerCase());

export default function OrdenesServicioPOPage() {

  const [productos, setProductos] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [productoEdit, setProductoEdit] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [openLeerPdf, setOpenLeerPdf] = useState(false);
  const [datosDesdeOcr, setDatosDesdeOcr] = useState(null);

  const appName = 'ordenes_servicio_ingresos_po';
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user.username;
  const permissionAccess = permissions.priv_access === 'Y';
  const permissionDelete = permissions.priv_delete === 'Y';
  const permissionUpdate = permissions.priv_update === 'Y';
  const permissionInsert = permissions.priv_insert === 'Y';
  const permissionExport = permissions.priv_export === 'Y';

  const cargarProductos = async () => {
    setLoading(true);
    try {
      const res = await getOrdenesServicioPO();
      setProductos(res);
    }
    catch {
      message.error("Error cargando el registro de productos");
    }
    setLoading(false);
  }

  useEffect(() => {
    cargarProductos();
  }, [])

  // Devuelve true/false para que el borrado múltiple de la tabla pueda contar
  // cuántos se eliminaron (un producto con entregas no se puede eliminar).
  const eliminar = async (record) => {
    try {
      await deleteOrdenServicioPO(record.oid, userLogin);
      return true;
    }
    catch (error) {
      message.error(error?.response?.data?.message || "Error eliminando registro");
      return false;
    }
  }

  const handleDelete = async (record) => {
    if (await eliminar(record)) message.success("Registro eliminado");
    cargarProductos();
  }

  const handleDeleteSelected = async (ids) => {
    let eliminados = 0;
    for (const oid of ids) {
      if (await eliminar({ oid })) eliminados++;
    }
    if (eliminados) message.success(`${eliminados} registro(s) eliminados`);
    cargarProductos();
  }

  const handleEdit = (record) => {
    if (!permissionUpdate) {
      message.warning("No tiene permisos para editar");
      return;
    }
    setDatosDesdeOcr(null);
    setProductoEdit(record);
    setOpenForm(true);
  };

  const handleCreate = () => {
    if (!permissionInsert) {
      message.warning("No tiene permisos para crear");
      return;
    }
    setDatosDesdeOcr(null);
    setProductoEdit(null);
    setOpenForm(true);
  }

  // El PDF de la PO precarga encabezado y productos; el usuario completa
  // proyecto, municipio y unidad antes de guardar.
  const handleUsarDatosOcr = (resultado) => {
    const fecha = resultado.fecha ? dayjs(resultado.fecha) : null;
    setDatosDesdeOcr({
      encabezado: {
        Numero_PO: resultado.numero_po || undefined,
        fecha_entrega_po: fecha?.isValid() ? fecha : undefined
      },
      productos: (resultado.lineas || []).map((l) => ({
        nombre_producto: l.nombre_producto_ocr,
        cantidad: l.cantidad ?? undefined,
        valor_unitario_COP: l.valor_unitario_COP ?? undefined
      }))
    });
    setProductoEdit(null);
    setOpenForm(true);
  };

  const filteredProductos = productos.filter((p) => {
    if (filters.search && !contiene(p.nombre_producto, filters.search) && !contiene(p.descripcion, filters.search)) return false;
    if (filters.po && !contiene(p.Numero_PO, filters.po)) return false;
    if (filters.proveedor && p.oid_proveedor !== filters.proveedor) return false;
    if (filters.proyecto && p.oid_proyecto !== filters.proyecto) return false;
    return true;
  });

  if (!permissionAccess) {
    return <div>No tiene permisos para acceder a este módulo</div>;
  }

  return (
    <ConfigProvider locale={esES}>
    <div className={`container ${styles.ingresosPoModule}`}>
      <OrdenesServicioPOHeader
        onCreate={handleCreate}
        onLeerPdf={() => setOpenLeerPdf(true)}
        onRefresh={cargarProductos}
        permissionInsert={permissionInsert}
      />
      <OrdenesServicioPOCards productos={filteredProductos} />
      <OrdenesServicioPOFilters
        onFilter={setFilters}
        filteredProductos={filteredProductos}
        permissionExport={permissionExport}
      />
      <OrdenesServicioPOTable
        data={filteredProductos}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDeleteSelected={handleDeleteSelected}
        permissionDelete={permissionDelete}
        permissionUpdate={permissionUpdate}
      />
      <RegistroProductosPOForm
        open={openForm}
        producto={productoEdit}
        initialData={datosDesdeOcr}
        onClose={() => setOpenForm(false)}
        onSuccess={cargarProductos}
        userLogin={userLogin}
      />
      <LeerPoPdfModal
        open={openLeerPdf}
        onClose={() => setOpenLeerPdf(false)}
        onUsarDatos={handleUsarDatosOcr}
      />
    </div>
    </ConfigProvider>
  )
}
